import React from 'react';
import { Box, Typography, Grid, Card, CardContent, Stack, useMediaQuery } from '@mui/material';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import TerrainIcon from '@mui/icons-material/Terrain';
import SpaIcon from '@mui/icons-material/Spa';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import BrushIcon from '@mui/icons-material/Brush';
import { Icon } from "@iconify/react";
import Stagger from './Stagger';
import Reveal from './Reveal';

const experiences = [
  {
    icon: <RestaurantIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />, 
    title: 'Gastronomía local',
    desc: 'Podés disfrutar de platos caseros, pastas, parrilla, carnes al disco, productos locales frescos.'
  },
  {
    icon: <TerrainIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />, 
    title: 'Turismo de aventura',
    desc: 'Vení a experimentar actividades como trekking, senderismo, ciclismo, escaladas.'
  },
  {
    icon: <Icon icon="mdi:horse-human" width="32" height="32" style={{color: '#2e7d32', marginBottom: '8px'}} />, 
    title: 'Turismo ecuestre',
    desc: 'Cabalgatas únicas entre paisajes serranos.'
  },
  {
    icon: <SpaIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />, 
    title: 'Relax y bienestar',
    desc: 'Paseos al aire libre, Yoga, Tai Chi, espacios únicos para relajarte, combinados con naturaleza y tranquilidad.'
  },
  {
    icon: <AccountBalanceIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />, 
    title: 'Cultura e historia',
    desc: 'La Granja conserva su herencia, descubrí capillas históricas, museos, encuentros culturales, ofreciendo experiencias únicas.'
  },
  {
    icon: <BrushIcon sx={{ fontSize: 32, color: 'warning.dark', mb: 1 }} />, 
    title: 'Feria de artesanías',
    desc: 'Descubrí la creatividad de nuestros artesanos en esta gran feria, ubicada en la Plaza Los Troncos, todos los sábados, domingos y feriados por la tarde.'
  },
  {
    icon: <CameraAltIcon sx={{ fontSize: 32, color: 'success.dark', mb: 1 }} />, 
    title: 'Fotografía y paisajes',
    desc: 'La Granja es un paraíso para los apasionados de la fotografía. Desde sierras, ríos y senderos naturales, hasta rincones históricos y culturales.'
  },
];

export default function Experiences() {
  const isMobile = useMediaQuery('(max-width:800px)');

  const cardContent = (exp) => (
    <Card sx={{ 
      borderRadius: 3, 
      boxShadow: 2, 
      height: 230, // Altura fija para todas las cards
      textAlign: 'center', 
      display: 'flex', 
      flexDirection: 'column', 
      maxWidth: 260, 
      mx: 'auto', 
      p: 1,
      transform: 'translate3d(0,0,0)',
      transition: 'transform 220ms ease, box-shadow 220ms ease',
      '&:hover': {
        transform: 'translate3d(0,-6px,0)',
        boxShadow: '0 14px 34px rgba(0,0,0,0.16)',
      },
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
        '&:hover': { transform: 'none' },
      },
      '&:hover .exp-icon': {
        transform: 'translate3d(0,-2px,0) scale(1.06)',
      }
    }}>
      <CardContent sx={{ 
        p: 2, 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start'
      }}>
        <Stack alignItems="center" spacing={1} sx={{ height: '100%' }}>
          {/* Icono pequeño arriba del título */}
          <Box
            className="exp-icon"
            sx={{
              mb: 1,
              transition: 'transform 220ms ease',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          >
            {React.cloneElement(exp.icon, { 
              sx: { 
                fontSize: 32, 
                ...exp.icon.props.sx,
                mb: 0
              } 
            })}
          </Box>
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            mb: 2, 
            color: 'primary.main', 
            fontSize: '1.4rem',
            lineHeight: 1.2,
            minHeight: '2.4rem', // Altura mínima para títulos
            display: 'flex',
            alignItems: 'center'
          }}>
            {exp.title}
          </Typography>
          <Typography variant="body2" color="text.primary" sx={{ 
            fontSize: 14, 
            fontWeight: 500,
            lineHeight: 1.4,
            flex: 1,
            display: 'flex',
            alignItems: 'flex-start'
          }}>
            {exp.desc}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ py: 8, background: 'transparent' }}>
      <Box maxWidth={1200} mx="auto" px={2}>
        <Stagger baseDelay={0} step={110} variants={['fade-down', 'fade-up', 'zoom']} distance={18}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Experiencias Únicas
          </Typography>
          <Typography variant="subtitle1" sx={{ mb: 4, color: 'text.secondary' }}>
            Vení a vivir momentos únicos y conéctate con la esencia natural de La Granja.
          </Typography>
          {isMobile ? (
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={16}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              style={{ paddingBottom: 40 }}
            >
              {experiences.map((exp, idx) => (
                <SwiperSlide key={idx}>
                  {cardContent(exp)}
                </SwiperSlide>
              ))}
            </Swiper>
          ) : (
            <Grid container spacing={3} rowSpacing={5} justifyContent="center" alignItems="stretch">
              {experiences.map((exp, idx) => (
                <Grid item md={3} key={idx} display="flex" justifyContent="center">
                  <Reveal
                    delay={120 + idx * 90}
                    variant={idx % 3 === 0 ? 'slide-left' : idx % 3 === 1 ? 'slide-right' : 'fade-up'}
                    distance={22}
                  >
                    {cardContent(exp)}
                  </Reveal>
                </Grid>
              ))}
            </Grid>
          )}
        </Stagger>
      </Box>
    </Box>
  );
}